import { cleanSpaces } from '@/lib/string'
import { objDiff } from '@/lib/utils'
import { getStoreState } from '@/redux/stores'
import { UserCreationPayload } from '@/travelsuit'
import { VoidCallback } from '@/travelsuit/callback'
import { prepareUserRoleJson } from '@/travelsuit/user-roles'
import { notNulllableFields, UserSearchParams } from '@/travelsuit/users'
import { UserCurrency } from '@/types/common'
import { Locales } from '@/types/locale'
import { User, UserRole } from '@/types/user'

import { IAction } from './action-helpers'
import { AdminUsersTypes as Types } from './actions.types'

function cleanUserNames<
	T extends { first_name?: string | null; middle_name?: string | null; last_name?: string | null },
>(user: T): T {
	return {
		...user,
		first_name: cleanSpaces(user.first_name),
		middle_name: cleanSpaces(user.middle_name),
		last_name: cleanSpaces(user.last_name),
	}
}

export const Actions = {
	getAllUsers(params: UserSearchParams = {}, callback?: VoidCallback<User[]>, errCallback?: VoidCallback): IAction {
		return {
			type: Types.GetAllUsers.REQUEST,
			params,
			callback,
			errCallback,
		}
	},
	createUser(user: Partial<UserCreationPayload>, callback?: VoidCallback<User>, errCallback?: VoidCallback): IAction {
		return {
			type: Types.AddUser.REQUEST,
			payload: cleanUserNames(user),
			callback,
			errCallback,
		}
	},
	updateUser(user: User, callback?: VoidCallback<User>, errCallback?: VoidCallback): IAction {
		let existingTraveler = getStoreState().adminUsers.get(user.id!)
		if (!existingTraveler && getStoreState().auth.get('internalUser').id === user.id) {
			existingTraveler = getStoreState().auth.get('internalUser')
		}

		const fullUser = {
			...existingTraveler,
			...user,
		}
		const cleanedFullUser = cleanUserNames(fullUser)
		const changes = objDiff(existingTraveler, cleanedFullUser)
		notNulllableFields.forEach((k) => {
			if (changes[k] === null) {
				delete changes[k]
			}
		})

		const { invoice_profile, ...changesToSend } = changes

		return {
			type: Types.UpdateUser.REQUEST,
			payload: { user: cleanedFullUser },
			body: { ...changesToSend, invoice_profile_id: invoice_profile?.id },
			callback,
			errCallback,
		}
	},
	updateUserLanguage(language: Locales, callback?: VoidCallback<User>, errCallback?: VoidCallback): IAction {
		const user = getStoreState().auth.get('internalUser')
		return {
			type: Types.UpdateUser.REQUEST,
			payload: { user: { ...user, language_code: language } },
			body: { language_code: language },
			callback,
			errCallback,
		}
	},
	updateUserCurrency(currency: UserCurrency, callback?: VoidCallback<User>, errCallback?: VoidCallback): IAction {
		const user = getStoreState().auth.get('internalUser')
		return {
			type: Types.UpdateUser.REQUEST,
			payload: { user: { ...user, currency_code: currency } },
			body: { currency_code: currency },
			callback,
			errCallback,
		}
	},
	getDeactivateInfo(userId: number, callback?: VoidCallback, errCallback?: VoidCallback): IAction {
		return {
			type: Types.GetDeactivationInfo.REQUEST,
			payload: userId,
			callback,
			errCallback,
		}
	},
	deactivateUser(user: User, callback?: VoidCallback, errCallback?: VoidCallback): IAction {
		return {
			type: Types.DeactivateUser.REQUEST,
			payload: user.id,
			callback,
			errCallback,
		}
	},
	removeUser(user: User, callback?: VoidCallback, errCallback?: VoidCallback): IAction {
		return {
			type: Types.RemoveUser.REQUEST,
			payload: user.id,
			callback,
			errCallback,
		}
	},
	addUserRole(user: User, userRole: UserRole, callback?: VoidCallback<UserRole>) {
		return {
			type: Types.AddUserRole.REQUEST,
			payload: { user, userRole },
			body: prepareUserRoleJson(userRole),
			callback,
		}
	},
	removeUserRole(user: User, userRole: UserRole, callback?: VoidCallback) {
		return {
			type: Types.RemoveUserRole.REQUEST,
			payload: { user, userRole },
			callback,
		}
	},
	invite(user: User, callback?: VoidCallback, errCalback?: VoidCallback) {
		return {
			type: Types.InviteUser.REQUEST,
			payload: { user: cleanUserNames(user) },
			callback,
			errCalback,
		}
	},
}
